import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinanceNote from "@/components/FinanceNote";
import { capitalPartners as c } from "@/content/capitalPartners";

const CONTACT_URL = "/contact?topic=capital-partners";

export default function CapitalPartners() {
  return (
    <>
      <Seo
        title="Bioregional Capital Partners | Regenerative Investment"
        description="Discover the regenerative investment initiative of Bioregional Ventures - committed capital being readied for productive land, enterprise and long-term stewardship."
      />

      {/* Hero - typographic sub-brand of Bioregional Ventures */}
      <section className="bg-eucalyptus text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <img
              src="/bcp-logo-white.png"
              alt="Bioregional Capital Partners logo"
              className="h-28 md:h-36 w-auto mb-10"
              data-testid="cp-logo"
            />
            <p className="text-xs uppercase tracking-[0.2em] text-cream/60" data-testid="cp-parent-brand">
              {c.parentBrand}
            </p>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight" data-testid="cp-heading">
              {c.name}
            </h1>
            <p className="mt-4 font-serif italic text-xl md:text-2xl text-ochre" data-testid="cp-standfirst">
              {c.standfirst}
            </p>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-cream/85 max-w-2xl">
              {c.heroBody}
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-cream/85 max-w-2xl" data-testid="cp-hero-body-second">
              {c.heroBodySecond}
            </p>
            <Link
              to={CONTACT_URL}
              data-testid="cp-hero-cta"
              className="mt-10 inline-block border border-cream/60 text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-cream hover:text-eucalyptus transition-colors duration-300"
            >
              {c.heroCta}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Investment themes */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cp-themes-heading">
              {c.themesHeading}
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.themes.map((theme, i) => (
              <Reveal key={theme.title} delay={i * 0.06} className="h-full">
                <div className="h-full border border-charcoal/15 bg-cream p-7" data-testid={`cp-theme-${i + 1}`}>
                  <h3 className="font-serif text-xl text-charcoal leading-snug">{theme.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/85">{theme.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            {c.themesNote && <p className="mt-8 text-xs text-charcoal/60 italic">{c.themesNote}</p>}
          </Reveal>
        </div>
      </section>

      {/* Investing in community wealth */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cp-community-wealth-heading">
              {c.communityWealthHeading}
            </h2>
            {c.communityWealthBody.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Investment principles */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal max-w-3xl" data-testid="cp-principles-heading">
            {c.principlesHeading}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-3xl" data-testid="cp-principles-intro">
            {c.principlesIntro}
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0 divide-y md:divide-y-0 divide-charcoal/10">
          {c.principles.map((p, i) => (
            <div key={p.title} className="py-5 md:border-b md:border-charcoal/10 flex gap-4" data-testid={`cp-principle-${i + 1}`}>
              <span className="font-serif text-ochre shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="font-medium text-charcoal">{p.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-charcoal/85">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
        <Reveal>
          <p className="mt-8 text-xs text-charcoal/60 italic" data-testid="cp-principles-note">{c.principlesNote}</p>
        </Reveal>
      </section>

      {/* Structure + partners */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <Reveal>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="cp-structure-heading">
              {c.structureHeading}
            </h2>
            {c.structureBody.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="cp-partners-heading">
              {c.partnersHeading}
            </h2>
            {c.partnersBody.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Relationship + current stage */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
        <Reveal>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="cp-relationship-heading">
            {c.relationshipHeading}
          </h2>
          {c.relationshipBody.map((p) => (
            <p key={p.slice(0, 24)} className="mt-5 text-base leading-relaxed text-charcoal/90">
              {p}
            </p>
          ))}
        </Reveal>
        <Reveal delay={0.08}>
          <div className="border border-charcoal/15 bg-cream p-7 md:p-9">
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="cp-stage-heading">
              {c.stageHeading}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-charcoal/90">{c.stageBody}</p>
            <Link
              to={CONTACT_URL}
              data-testid="cp-stage-cta"
              className="mt-8 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
            >
              {c.stageCta}
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Required disclosure */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <p className="border-l-2 border-ochre pl-4 text-sm leading-relaxed text-charcoal/80 max-w-3xl" data-testid="cp-required-disclosure">
          {c.disclosure}
        </p>
        <FinanceNote />
      </section>
    </>
  );
}
